/**
 * useInsights
 *
 * Returns the narrative insight cards rendered by InsightsSection on the
 * Progress page. Backend endpoint: GET /analytics/me/insights (LLM-backed,
 * cached server-side for 24h per user / period / UTC day).
 *
 * Mode switching mirrors the canonical pattern from `useDailyPlan` in
 * useRevision.ts - in `mock` mode we keep using the local rule engine
 * so the page works fully offline; in `empty` mode we return `[]` so
 * InsightsSection renders its empty placeholder card; in `api` mode we
 * call the real endpoint.
 */

import { useQuery } from '@tanstack/react-query';
import {
  progressApi,
  type ApiInsight,
  type InsightsResponse,
  type MyProgressResponse,
  type ProgressPeriod,
} from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { loadMockProgressData } from '@/data';
import type { ProgressInsight } from '@/types/progress';

interface UseInsightsOptions {
  /** Period the insights should reflect. */
  period: ProgressPeriod;
  /** Progress snapshot - needed for the mock-mode rule engine fallback.
   *  In API mode the backend builds its own snapshot, so this is optional. */
  progress?: MyProgressResponse;
  /** Caller-controlled gate (e.g. suppressed on past-year views). Defaults
   *  to true; ANDed with the internal mock-mode readiness gate. */
  enabled?: boolean;
}

/** Backend returns `title: string | null`; the InsightsSection prop type
 *  uses `title?: string`. Normalize once at the hook boundary. */
function adaptApiInsights(items: ApiInsight[]): ProgressInsight[] {
  return items.map(i => ({
    title: i.title ?? undefined,
    body: i.body,
    sentiment: i.sentiment,
  }));
}

export function useInsights({
  period,
  progress,
  enabled = true,
}: UseInsightsOptions) {
  const { isMock, isEmpty, isApi } = useDataMode();

  // In mock mode the feed is derived from the progress snapshot, so the
  // query must not run before that snapshot has loaded - otherwise it
  // resolves to `[]` and React Query caches it (staleTime below) without
  // ever refetching once the data arrives, leaving the section empty.
  // API/empty modes don't depend on the snapshot.
  const ready = !isMock || progress !== undefined;

  return useQuery<ProgressInsight[]>({
    // Including mode flags in the key so a mid-session VITE_DATA_MODE
    // change refetches against the right source rather than serving a
    // stale cache from the previous branch.
    queryKey: ['insights', period, isApi, isMock, isEmpty],
    enabled: enabled && ready,
    queryFn: async () => {
      if (isEmpty) return [];
      if (isMock)
        return progress
          ? (await loadMockProgressData()).buildMockInsights(progress)
          : [];
      const res: InsightsResponse = await progressApi.getInsights(period);
      return adaptApiInsights(res.insights);
    },
    // 30 min on the client - server's daily cache is the source of
    // truth, this just keeps period-toggling and tab-switches snappy
    // without hammering the endpoint.
    staleTime: 30 * 60 * 1000,
  });
}
